<template>
  <UModal
    :open="isVisible"
    :title="title"
    :close="false"
    :dismissible="closeOnRequest"
    :ui="{
      overlay: 'z-50 bg-black/50',
      content: 'z-50 max-w-md rounded-2xl bg-surface shadow-xl divide-y-0',
      header: 'p-6 pb-0 min-h-0',
      title: ['text-xl font-semibold flex items-center gap-3', ui?.title],
      body: 'p-6',
    }"
    @update:open="onOpenChange"
    @close:prevent="emit('close')"
  >
    <!-- Forward only when given: UModal renders its header whenever a title
         slot exists, so an unconditional one would add an empty padded header
         to untitled dialogs. -->
    <template
      v-if="$slots.title"
      #title
    >
      <slot name="title" />
    </template>
    <template #body>
      <slot />
    </template>
  </UModal>
</template>

<script setup lang="ts">
const isVisible = defineModel<boolean>('isVisible', { default: false });

withDefaults(defineProps<{
  // Either of these makes UModal render its own DialogTitle, which is what
  // supplies the dialog's accessible name. Use the slot for an icon heading.
  title?: string;
  closeOnRequest?: boolean;
  // Per-dialog heading tone, e.g. { title: 'text-error' }, merged over the
  // shared heading classes above.
  ui?: { title?: string };
}>(), {
  title: undefined,
  closeOnRequest: true,
  ui: () => ({}),
});

const emit = defineEmits<{
  (e: 'close'): void
}>();

// UModal reports a dismissal as update:open(false); mirror it back onto
// isVisible and re-emit `close` so callers keep the same contract. When
// closeOnRequest is false the modal refuses to dismiss and reports
// close:prevent instead, so the parent still hears the attempt while the
// dialog stays open.
function onOpenChange(value: boolean) {
  isVisible.value = value;
  if (!value) emit('close');
}
</script>
